import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from './utils/axiosConfig';
import Lightbox from 'react-image-lightbox'; // 匯入 Lightbox
import 'react-image-lightbox/style.css'; // 匯入 Lightbox 的樣式
import { Form, Spinner, Container, Row, Col } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { MEMBER_TERM } from './config/clubTerms';

// 防抖自定義 Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

// 骨架屏組件
const SkeletonCard = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'pulse 1.5s ease-in-out infinite alternate',
  }}>
    <div style={{
      width: '60px',
      height: '80px',
      backgroundColor: '#e0e0e0',
      borderRadius: '8px',
      marginBottom: '4px',
    }}></div>
    <div style={{
      width: '50px',
      height: '12px',
      backgroundColor: '#e0e0e0',
      marginBottom: '2px',
    }}></div>
    <div style={{
      width: '40px',
      height: '12px',
      backgroundColor: '#e0e0e0',
      marginBottom: '2px',
    }}></div>
    <div style={{
      width: '45px',
      height: '12px',
      backgroundColor: '#e0e0e0',
    }}></div>
  </div>
);

// 記憶化的員工卡片組件
const EmployeeCard = React.memo(({ employee, index, onImageClick, isMobile, refreshKey }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = React.useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // 提前100px開始載入
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  // 響應式尺寸
  const imageWidth = isMobile ? '80px' : '60px';
  const imageHeight = isMobile ? '106px' : '80px';
  const nameFontSize = isMobile ? '13px' : '12px';
  const infoFontSize = isMobile ? '12px' : '11px';

  return (
    <div
      ref={imgRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        lineHeight: '1.2',
        margin: 0,
        padding: isMobile ? '8px' : '0',
        transition: 'transform 0.2s ease-in-out',
        backgroundColor: isMobile ? 'rgba(255,255,255,0.9)' : 'transparent',
        borderRadius: isMobile ? '12px' : '0',
        boxShadow: isMobile ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      {!imageLoaded && !imageError && <SkeletonCard />}

      {!imageError ? (
        isVisible && (
          <img
            src={`/uploads/EDM/${employee.EDM_photo}${refreshKey > 0 ? `?v=${refreshKey}` : ''}`}
            alt={employee.e_name}
            style={{
              width: imageWidth,
              height: imageHeight,
              objectFit: 'cover',
              borderRadius: '8px',
              marginBottom: '4px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: imageLoaded ? 'block' : 'none',
            }}
            onLoad={handleImageLoad}
            onError={(e) => {
              console.log('圖片載入失敗:', employee.EDM_photo, '完整路徑:', e.target.src);
              handleImageError();
            }}
            onClick={() => onImageClick(`/uploads/EDM/${employee.EDM_photo}`)}
          />
        )
      ) : (
        <div style={{
          width: imageWidth,
          height: imageHeight,
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: '#999',
        }}>
          無圖片
        </div>
      )}

      <p style={{ margin: 0, fontWeight: 'bold', fontSize: nameFontSize }}>{employee.store_name}</p>
      <p style={{ margin: 0, fontWeight: 'bold', fontSize: infoFontSize }}>{employee.position_title}</p>
      <p style={{ margin: 0, fontSize: infoFontSize }}>{employee.e_name}</p>
    </div>
  );
});

const EDMPhotoWall = () => {
  const [employees, setEmployees] = useState([]);
  const [storeNames, setStoreNames] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [cache, setCache] = useState({});
  const [imageRefreshKey, setImageRefreshKey] = useState(0); // 用於強制刷新圖片

  const token = localStorage.getItem('token');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // 響應式判斷：768px 以下顯示手機版優化佈局
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1200 });

  // 獲取所有 store_name 用於下拉選單
  const fetchStoreNames = useCallback(async () => {
    const cacheKey = 'storeNames';
    if (cache[cacheKey]) {
      setStoreNames(cache[cacheKey]);
      return;
    }

    try {
      const response = await axios.get('/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setStoreNames(response.data);
      setCache(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (error) {
      console.error('Error fetching store names:', error);
    }
  }, [token, cache]);

  const fetchEmployees = useCallback(async (storeFilter) => {
    const cacheKey = `employees-${storeFilter}`;
    if (cache[cacheKey]) {
      setEmployees(cache[cacheKey]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get('/employees/edm-photos', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          filter: '', // 先不用搜尋過濾，在前端處理
          limit: 1000
        },
      });
  
      // 獲取所有有 EDM_photo 的員工
      let employeesWithPhotos = response.data.data || [];
      console.log('API 回傳的員工資料:', employeesWithPhotos.length, employeesWithPhotos.slice(0, 3));
      
      // 如果有指定社別，則進一步過濾
      if (storeFilter) {
        employeesWithPhotos = employeesWithPhotos.filter(employee => 
          employee.store_name === storeFilter
        );
        console.log(`社別 ${storeFilter} 篩選後:`, employeesWithPhotos.length);
      }
      
      setEmployees(employeesWithPhotos);
      setCache(prev => ({ ...prev, [cacheKey]: employeesWithPhotos }));
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [token, cache]);
  

  // 處理 Lightbox 圖片點擊（含預載入）
  const handleImageClick = useCallback((imageSrc) => {
    // 預載入圖片
    const img = new Image();
    img.onload = () => {
      setLightboxImage(imageSrc);
      setIsLightboxOpen(true);
    };
    img.onerror = () => {
      console.error('Lightbox 圖片載入失敗:', imageSrc);
      // 即使失敗也顯示 Lightbox（讓 Lightbox 處理錯誤）
      setLightboxImage(imageSrc);
      setIsLightboxOpen(true);
    };
    img.src = imageSrc;
  }, []);

  // 過濾後的員工資料（基於搜尋和社別篩選）
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const matchesSearch = !debouncedSearch || 
        employee.e_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        employee.position_title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        employee.store_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (employee.industry_keywords && employee.industry_keywords.toLowerCase().includes(debouncedSearch.toLowerCase()));
      
      const matchesStore = !selectedStore || employee.store_name === selectedStore;
      
      return matchesSearch && matchesStore;
    });
  }, [employees, debouncedSearch, selectedStore]);

  // 初始加載 store_name 列表和員工資料
  useEffect(() => {
    const initializeData = async () => {
      await fetchStoreNames();
      await fetchEmployees(selectedStore);
    };
    initializeData();
  }, []); // 只在組件首次載入時執行

  // 當選擇的社別變更時重新載入員工資料
  useEffect(() => {
    if (selectedStore !== '') { // 避免初始空值觸發
      fetchEmployees(selectedStore);
    }
  }, [selectedStore]);

  // 處理 store_name 下拉選單變更
  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value);
  };

  // 處理搜尋輸入變更
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 計算響應式 Grid 列數
  const getGridColumns = () => {
    if (isMobile) return 'repeat(auto-fill, minmax(100px, 1fr))'; // 手機：較少列
    if (isTablet) return 'repeat(auto-fill, minmax(110px, 1fr))'; // 平板：中等列
    return 'repeat(auto-fill, minmax(120px, 1fr))'; // 桌面：更多列
  };

  return (
    <Container fluid style={{
      padding: isMobile ? '12px' : '20px',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      {/* 標題區域 */}
      <Row className="mb-4">
        <Col xs={12} className="text-center">
          <h2 style={{
            color: '#2c3e50',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            fontSize: isMobile ? '20px' : '28px'
          }}>
            {MEMBER_TERM}服務及產品海報
          </h2>
        </Col>
      </Row>

      {/* 篩選控制區域 */}
      <Row className="mb-4">
        <Col xs={12} md={6} className="mb-3">
          <Form.Group>
            <Form.Label style={{
              fontWeight: 'bold',
              color: '#34495e',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              選擇社別:
            </Form.Label>
            <Form.Select
              value={selectedStore}
              onChange={handleStoreChange}
              className={isMobile ? 'form-select-lg' : ''}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              <option value="">全部社別</option>
              {storeNames.map((store) => (
                <option key={store.ss_id} value={store.ss_name}>{store.ss_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={5} className="mb-3">
          <Form.Group>
            <Form.Label style={{
              fontWeight: 'bold',
              color: '#34495e',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              搜尋{MEMBER_TERM}+媒合找尋:
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={isMobile ? "搜尋..." : "搜尋姓名、職位、社別或行業關鍵字..."}
              value={searchQuery}
              onChange={handleSearchChange}
              className={isMobile ? 'form-control-lg' : ''}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={2} className="mb-3 d-flex align-items-end">
          <button
            onClick={() => {
              setCache({});
              setEmployees([]);
              setImageRefreshKey(Date.now()); // 強制刷新所有圖片
              fetchEmployees(selectedStore);
            }}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: isMobile ? '12px 20px' : '10px 15px',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              fontWeight: 'bold',
              width: isMobile ? '100%' : 'auto',
              fontSize: isMobile ? '16px' : '14px'
            }}
          >
            🔄 刷新圖片
          </button>
        </Col>
      </Row>

      {/* 統計資訊 */}
      <Row className="mb-3">
        <Col xs={12} className="text-center">
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: '15px',
            padding: isMobile ? '8px' : '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#7f8c8d' }}>
              顯示 {filteredEmployees.length} 位{MEMBER_TERM}的服務海報
              {selectedStore && ` (${selectedStore})`}
              {debouncedSearch && ` | 媒合搜尋: "${debouncedSearch}"`}
            </span>
          </div>
        </Col>
      </Row>

      {/* 載入狀態 */}
      {loading && (
        <Row className="mb-4">
          <Col xs={12} className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: isMobile ? '2.5rem' : '3rem', height: isMobile ? '2.5rem' : '3rem' }} />
            <div style={{ marginTop: '10px', color: '#7f8c8d', fontSize: isMobile ? '14px' : '16px' }}>載入中...</div>
          </Col>
        </Row>
      )}

      {/* EDM 照片牆 */}
      <Row>
        <Col xs={12}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: getGridColumns(),
            gap: isMobile ? '15px' : '20px',
            textAlign: 'center',
            padding: isMobile ? '5px' : '10px'
          }}>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee, index) => (
                <EmployeeCard
                  key={`${employee.e_id}-${index}`}
                  employee={employee}
                  index={index}
                  onImageClick={handleImageClick}
                  isMobile={isMobile}
                  refreshKey={imageRefreshKey}
                />
              ))
            ) : !loading && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: isMobile ? '30px 20px' : '40px',
                color: '#7f8c8d',
                fontSize: isMobile ? '16px' : '18px'
              }}>
                {debouncedSearch || selectedStore ? `沒有找到符合媒合條件的${MEMBER_TERM}` : `目前沒有${MEMBER_TERM}上傳服務海報`}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Lightbox 顯示區域 */}
      {isLightboxOpen && (
        <Lightbox
          mainSrc={lightboxImage}
          onCloseRequest={() => setIsLightboxOpen(false)}
        />
      )}

      {/* CSS 動畫 */}
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Container>
  );
};

export default EDMPhotoWall;
